import { lazy, Suspense, useEffect, useState } from "react";
import { useConsent } from "@/hooks/useConsent";
import { Button } from "@/components/ui/button";
import { useApp } from "@/hooks/useApp";

const ConsentSettings = lazy(() => import("./ConsentSettings").then((m) => ({ default: m.ConsentSettings })));

/**
 * Bottom non-modal banner, LGPD opt-in strict.
 * - Three options of equal visual weight: Reject non-essential | Customize | Accept all
 * - Keyboard accessible (rendered as <section role="dialog" aria-label>)
 * - Fully responsive
 * - Reopenable via window.event 'open-consent-settings'
 */
export function ConsentBanner(): JSX.Element | null {
  const { isDecided } = useConsent();
  const { lang } = useApp();
  const [showSettings, setShowSettings] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Defer mount past first paint so the banner never blocks LCP.
    const id = setTimeout(() => setMounted(true), 400);
    const handler = () => setShowSettings(true);
    window.addEventListener("open-consent-settings", handler);
    return () => {
      clearTimeout(id);
      window.removeEventListener("open-consent-settings", handler);
    };
  }, []);

  if (!mounted) return null;

  const t = lang === "en" ? COPY.en : COPY.pt;

  return (
    <>
      {!isDecided && (
        <section
          role="dialog"
          aria-label={t.aria}
          aria-modal="false"
          className="fixed inset-x-0 bottom-0 z-[80] border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-lg"
        >
          <div className="container mx-auto max-w-5xl px-4 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 text-sm">
              <p className="font-medium">{t.title}</p>
              <p className="text-muted-foreground mt-1">
                {t.body}{" "}
                <a href="/politicas" className="underline underline-offset-2">{t.link}</a>.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:flex-shrink-0">
              <BannerButton onClick={() => setShowSettings(true)}>{t.customize}</BannerButton>
              <BannerButton primary onClick={() => triggerAccept()}>{t.accept}</BannerButton>
            </div>
          </div>
        </section>
      )}
      <Suspense fallback={null}>
        {showSettings && <ConsentSettings open={showSettings} onOpenChange={setShowSettings} />}
      </Suspense>
    </>
  );
}

function BannerButton({
  onClick,
  primary,
  children,
}: {
  onClick: () => void;
  primary?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Button
      onClick={onClick}
      variant={primary ? "default" : "outline"}
      size="sm"
      className="w-full sm:w-auto justify-center"
    >
      {children}
    </Button>
  );
}

// Triggers without importing consent module directly into render (lazy access).
function triggerAccept(): void {
  void import("@/lib/observability").then(({ consent }) => consent.acceptAll());
}

const COPY = {
  pt: {
    aria: "Aviso de privacidade",
    title: "Sua privacidade importa",
    body: "Usamos cookies e ferramentas de análise para melhorar a experiência. Você pode personalizar a qualquer momento. Saiba mais na nossa",
    link: "Política de Privacidade",
    accept: "Aceitar tudo",
    customize: "Personalizar",
  },
  en: {
    aria: "Privacy notice",
    title: "Your privacy matters",
    body: "We use cookies and analytics to improve the experience. You can customize anytime. Read more in our",
    link: "Privacy Policy",
    accept: "Accept all",
    customize: "Customize",
  },
} as const;
